import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const D = {
  surface:'#161614', surface2:'#1c1b19', surface3:'#222220',
  border:'#2a2927', text:'#f0ede8', text2:'#a8a49e', text3:'#6b6966',
  accent:'#c49a3c', accentL:'rgba(196,154,60,0.12)', accentB:'rgba(196,154,60,0.25)',
  red:'#e05252', redL:'rgba(224,82,82,0.12)',
};
const inp  = { padding:'10px 14px', borderRadius:'8px', border:`1px solid ${D.border}`, fontSize:'14px', color:D.text, background:D.surface2, outline:'none', fontFamily:"'DM Sans',sans-serif", width:'100%', boxSizing:'border-box' };
const sel  = { ...inp, cursor:'pointer' };
const btnP = { background:D.accent, color:'#0e0e0d', border:'none', padding:'10px 20px', borderRadius:'8px', fontSize:'13px', fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" };
const btnS = { background:D.surface2, color:D.text2, border:`1px solid ${D.border}`, padding:'10px 20px', borderRadius:'8px', fontSize:'13px', cursor:'pointer', fontFamily:"'DM Sans',sans-serif" };
const btnD = { background:D.redL, color:D.red, border:'1px solid rgba(224,82,82,0.25)', padding:'10px 20px', borderRadius:'8px', fontSize:'13px', cursor:'pointer', fontFamily:"'DM Sans',sans-serif" };
const lbl  = { fontSize:'11px', fontWeight:600, color:D.text3, textTransform:'uppercase', letterSpacing:'0.6px', display:'block', marginBottom:'6px' };
const card = { background:D.surface, borderRadius:'12px', border:`1px solid ${D.border}` };
const overlay = { position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px', backdropFilter:'blur(4px)' };
const modal = { background:D.surface, borderRadius:'16px', border:`1px solid ${D.border}`, width:'100%', maxWidth:'520px', maxHeight:'90vh', overflow:'hidden', display:'flex', flexDirection:'column', boxShadow:'0 8px 32px rgba(0,0,0,0.6)' };
const mHead = { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'20px 24px', borderBottom:`1px solid ${D.border}` };
const mBody = { padding:'24px', overflowY:'auto', flex:1, display:'flex', flexDirection:'column', gap:'16px' };
const mFoot = { display:'flex', justifyContent:'flex-end', gap:'10px', padding:'16px 24px', borderTop:`1px solid ${D.border}` };

// ── Student multi-select toggle list ──────────────────────────────────────
function StudentPicker({ students, selected, onChange }) {
  const toggle = id => {
    if (selected.includes(id)) onChange(selected.filter(x => x !== id));
    else onChange([...selected, id]);
  };
  if (!students.length) return (
    <p style={{ fontSize:'12px', color:D.text3, margin:0, fontStyle:'italic' }}>No students found.</p>
  );
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'5px', maxHeight:'200px', overflowY:'auto' }}>
      {students.map(s => {
        const on = selected.includes(s.id);
        return (
          <div key={s.id} onClick={() => toggle(s.id)}
            style={{ display:'flex', alignItems:'center', gap:'10px', padding:'9px 12px',
              borderRadius:'8px', cursor:'pointer',
              border:`1px solid ${on ? D.accent : D.border}`,
              background: on ? D.accentL : D.surface2 }}>
            <div style={{ width:'28px', height:'28px', borderRadius:'6px', flexShrink:0,
              background: on ? D.accentL : D.surface3,
              border:`1px solid ${on ? D.accentB : D.border}`,
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:'12px', fontWeight:600, color: on ? D.accent : D.text3 }}>
              {s.full_name[0]}
            </div>
            <span style={{ fontSize:'13px', fontWeight: on ? 500 : 400, color: on ? D.text : D.text2, flex:1 }}>
              {s.full_name}
            </span>
            {on && <span style={{ color:D.accent, fontSize:'14px', fontWeight:700 }}>✓</span>}
          </div>
        );
      })}
    </div>
  );
}

export default function MediaPage() {
  const { isAdmin } = useAuth();
  const fileRef = useRef(null);

  const [media,    setMedia]    = useState([]);
  const [students, setStudents] = useState([]);
  const [taals,    setTaals]    = useState([]);
  const [types,    setTypes]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [loadErr,  setLoadErr]  = useState('');

  const [open,    setOpen]    = useState(false);
  const [editRec, setEdit]    = useState(null);
  const [title,   setTitle]   = useState('');
  const [stuIds,  setStuIds]  = useState([]);   // ← now an array
  const [taalId,  setTaal]    = useState('');
  const [typeId,  setType]    = useState('');
  const [notes,   setNotes]   = useState('');
  const [file,    setFile]    = useState(null);
  const [err,     setErr]     = useState('');
  const [saving,  setSaving]  = useState(false);
  const [pct,     setPct]     = useState(0);
  const [delTgt,  setDel]     = useState(null);
  const [filter,  setFilter]  = useState('');

  const load = async () => {
    setLoading(true);
    setLoadErr('');
    const errors = [];

    const safe = async (key, fn) => {
      try { await fn(); }
      catch (e) { errors.push(`${key}: ${e.response?.status ?? ''} ${e.response?.data?.error || e.message}`); }
    };

    await safe('taals',    async () => { const r = await axios.get('/api/taals');    setTaals(r.data); });
    await safe('types',    async () => { const r = await axios.get('/api/types');    setTypes(r.data); });
    if (isAdmin) { await safe('students', async () => { const r = await axios.get('/api/students'); setStudents(r.data); }); }
    await safe('media',    async () => { const r = await axios.get('/api/media');    setMedia(r.data); });

    if (errors.length) setLoadErr(errors.join(' | '));
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const openAdd = () => {
    setEdit(null); setTitle(''); setStuIds([]); setTaal(''); setType('');
    setNotes(''); setFile(null); setErr(''); setPct(0);
    if (fileRef.current) fileRef.current.value = '';
    setOpen(true);
  };

  const openEdit = rec => {
    setEdit(rec);
    setTitle(rec.title);
    setStuIds((rec.students || []).map(s => s.id));
    setTaal(String(rec.taal_id   || ''));
    setType(String(rec.type_id   || ''));
    setNotes(rec.notes || '');
    setFile(null); setErr('');
    setOpen(true);
  };

  const save = async () => {
    setErr('');
    if (!title.trim()) return setErr('Title is required.');
    setSaving(true);
    try {
      if (editRec) {
        await axios.put(`/api/media/${editRec.id}`, {
          title:      title.trim(),
          taal_id:    taalId  || null,
          type_id:    typeId  || null,
          notes:      notes   || null,
          student_ids: stuIds,
        });
      } else {
        const fd = new FormData();
        fd.append('title', title.trim());
        // Append each student id as a separate field
        stuIds.forEach(id => fd.append('student_ids[]', id));
        if (taalId) fd.append('taal_id', taalId);
        if (typeId) fd.append('type_id', typeId);
        if (notes)  fd.append('notes',   notes);
        if (file)   fd.append('file',    file);
        await axios.post('/api/media', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: e => setPct(Math.round(e.loaded * 100 / e.total)),
        });
      }
      setOpen(false); setPct(0); load();
    } catch (e) {
      setErr(e.response?.data?.error || `Error: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    try { await axios.delete(`/api/media/${delTgt.id}`); setDel(null); load(); }
    catch (e) { console.error(e); }
  };

  const isAud = u => u && /\.(mp3|wav|m4a|aac|ogg)$/i.test(u);
  const isVid = u => u && /\.(mp4|mov|avi|mkv|webm)$/i.test(u);
  const fmtD  = d => new Date(d).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
  const fmtSz = b => b < 1048576 ? `${(b/1024).toFixed(0)} KB` : `${(b/1048576).toFixed(1)} MB`;

  const shown = media.filter(m => {
    if (!filter) return true;
    return (m.students || []).some(s => String(s.id) === filter);
  });

  return (
    <div style={{ fontFamily:"'DM Sans',sans-serif" }}>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'20px', flexWrap:'wrap', gap:'12px' }}>
        <div>
          <h2 style={{ fontSize:'22px', fontWeight:600, color:D.text, margin:0 }}>{isAdmin ? 'Media Library' : 'My Media'}</h2>
          <p style={{ fontSize:'13px', color:D.text3, margin:'4px 0 0' }}>{shown.length} file{shown.length !== 1 ? 's' : ''}</p>
        </div>
        <div style={{ display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap' }}>
          {isAdmin && students.length > 0 && (
            <select style={{ ...sel, minWidth:'160px', width:'auto' }} value={filter} onChange={e => setFilter(e.target.value)}>
              <option value="">All Students</option>
              {students.map(s => <option key={s.id} value={String(s.id)}>{s.full_name}</option>)}
            </select>
          )}
          {isAdmin && <button style={btnP} onClick={openAdd}>+ Upload Media</button>}
        </div>
      </div>

      {/* Error banner */}
      {loadErr && (
        <div style={{ background:D.redL, border:`1px solid ${D.red}`, borderRadius:'8px', padding:'12px 16px', marginBottom:'16px', fontSize:'12px', color:D.red, fontFamily:'monospace' }}>
          <strong>API errors:</strong> {loadErr}
          <button style={{ ...btnS, padding:'4px 12px', fontSize:'12px', marginLeft:'12px' }} onClick={load}>Retry</button>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div style={{ textAlign:'center', color:D.text3, padding:'48px' }}>Loading...</div>
      ) : shown.length === 0 && !loadErr ? (
        <div style={{ ...card, padding:'48px', textAlign:'center' }}>
          <div style={{ fontSize:'32px', marginBottom:'12px', opacity:0.3 }}>♫</div>
          <p style={{ fontSize:'15px', fontWeight:500, color:D.text, marginBottom:'6px' }}>
            {isAdmin ? 'No media uploaded yet' : 'No media here yet'}
          </p>
          {isAdmin && <button style={{ ...btnP, marginTop:'8px' }} onClick={openAdd}>+ Upload First File</button>}
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:'14px' }}>
          {shown.map(rec => {
            const url = rec.audio_s3_key;
            return (
              <div key={rec.id} style={{ ...card, padding:'18px', display:'flex', flexDirection:'column', gap:'10px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span style={{ fontSize:'20px', opacity:0.5 }}>{isVid(url) ? '▶' : isAud(url) ? '♫' : '◻'}</span>
                  {isAdmin && (
                    <div style={{ display:'flex', gap:'5px' }}>
                      <button style={{ ...btnS, padding:'4px 10px', fontSize:'12px' }} onClick={() => openEdit(rec)}>Edit</button>
                      <button style={{ ...btnD, padding:'4px 10px', fontSize:'12px' }} onClick={() => setDel(rec)}>Del</button>
                    </div>
                  )}
                </div>

                <h3 style={{ fontSize:'14px', fontWeight:500, color:D.text, margin:0 }}>{rec.title}</h3>

                {/* Student badges — now can be multiple */}
                {rec.students?.length > 0 && (
                  <div style={{ display:'flex', gap:'5px', flexWrap:'wrap' }}>
                    {rec.students.map(s => (
                      <span key={s.id} style={{ background:D.surface2, border:`1px solid ${D.border}`, color:D.text2, padding:'2px 9px', borderRadius:'20px', fontSize:'11px' }}>
                        {s.full_name}
                      </span>
                    ))}
                  </div>
                )}

                <div style={{ display:'flex', gap:'5px', flexWrap:'wrap' }}>
                  {rec.taal       && <span style={{ background:D.accentL, border:`1px solid ${D.accentB}`, color:D.accent, padding:'2px 9px', borderRadius:'20px', fontSize:'11px' }}>{rec.taal.name}</span>}
                  {rec.lessonType && <span style={{ background:D.surface3, border:`1px solid ${D.border}`, color:D.text3, padding:'2px 9px', borderRadius:'20px', fontSize:'11px' }}>{rec.lessonType.name}</span>}
                </div>

                {rec.notes && <p style={{ fontSize:'12px', color:D.text3, margin:0, fontStyle:'italic' }}>{rec.notes}</p>}
                {url && isAud(url) && <audio controls style={{ width:'100%' }}><source src={url}/></audio>}
                {url && isVid(url) && <video controls style={{ width:'100%', borderRadius:'6px', maxHeight:'160px' }}><source src={url}/></video>}
                {!url && <div style={{ fontSize:'12px', color:D.text3, fontStyle:'italic' }}>No file attached</div>}
                <div style={{ fontSize:'11px', color:D.text3, marginTop:'auto' }}>{fmtD(rec.createdAt)}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add / Edit Modal */}
      {open && (
        <div style={overlay} onClick={e => e.target === e.currentTarget && setOpen(false)}>
          <div style={modal}>
            <div style={mHead}>
              <h3 style={{ margin:0, fontSize:'16px', fontWeight:600, color:D.text }}>{editRec ? 'Edit Recording' : 'Upload Media'}</h3>
              <button style={{ background:D.surface2, border:`1px solid ${D.border}`, borderRadius:'6px', padding:'5px 9px', cursor:'pointer', color:D.text2 }} onClick={() => setOpen(false)}>✕</button>
            </div>
            <div style={mBody}>

              {/* Title */}
              <div>
                <label style={lbl}>Title *</label>
                <input style={inp} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Teental Kaida practice"/>
              </div>

              {/* Students — multi-select toggle list */}
              <div>
                <label style={lbl}>
                  Assign to Students
                  {stuIds.length > 0 && (
                    <span style={{ background:D.accent, color:'#0e0e0d', borderRadius:'20px', padding:'1px 7px', fontSize:'10px', fontWeight:700, marginLeft:'6px', textTransform:'none', letterSpacing:0 }}>
                      {stuIds.length} selected
                    </span>
                  )}
                </label>
                <StudentPicker students={students} selected={stuIds} onChange={setStuIds}/>
              </div>

              {/* Taal + Type */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                <div>
                  <label style={lbl}>Taal</label>
                  <select style={sel} value={taalId} onChange={e => setTaal(e.target.value)}>
                    <option value="">— None —</option>
                    {taals.map(t => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Type</label>
                  <select style={sel} value={typeId} onChange={e => setType(e.target.value)}>
                    <option value="">— None —</option>
                    {types.map(t => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
                  </select>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label style={lbl}>Notes</label>
                <textarea style={{ ...inp, resize:'vertical', height:'72px' }} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes..."/>
              </div>

              {/* File — create only */}
              {!editRec && (
                <div>
                  <label style={lbl}>File (optional)</label>
                  <input ref={fileRef} id="mf" type="file" accept="audio/*,video/*" onChange={e => setFile(e.target.files[0] || null)} style={{ display:'none' }}/>
                  <label htmlFor="mf" style={{ ...inp, cursor:'pointer', textAlign:'center', border:`1px dashed ${D.border}`, color: file ? D.text : D.text3 }}>
                    {file ? `${file.name} (${fmtSz(file.size)})` : 'Click to choose audio or video file'}
                  </label>
                  {saving && pct > 0 && (
                    <div style={{ height:'4px', background:D.surface3, borderRadius:'2px', overflow:'hidden', marginTop:'6px' }}>
                      <div style={{ height:'100%', width:`${pct}%`, background:D.accent, transition:'width 0.3s' }}/>
                    </div>
                  )}
                  <p style={{ fontSize:'11px', color:D.text3, margin:'4px 0 0' }}>MP4, MOV, MP3, WAV, M4A, AAC, OGG — up to 500 MB</p>
                </div>
              )}

              {err && <div style={{ background:D.redL, border:'1px solid rgba(224,82,82,0.25)', borderRadius:'8px', padding:'10px 14px', fontSize:'13px', color:D.red }}>{err}</div>}
            </div>
            <div style={mFoot}>
              <button style={btnS} onClick={() => setOpen(false)}>Cancel</button>
              <button style={btnP} onClick={save} disabled={saving}>
                {saving ? (pct > 0 ? `Uploading ${pct}%` : 'Saving...') : editRec ? 'Save Changes' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {delTgt && (
        <div style={overlay} onClick={e => e.target === e.currentTarget && setDel(null)}>
          <div style={{ ...modal, maxWidth:'400px' }}>
            <div style={mHead}>
              <h3 style={{ margin:0, fontSize:'16px', fontWeight:600, color:D.text }}>Delete Recording</h3>
              <button style={{ background:D.surface2, border:`1px solid ${D.border}`, borderRadius:'6px', padding:'5px 9px', cursor:'pointer', color:D.text2 }} onClick={() => setDel(null)}>✕</button>
            </div>
            <div style={{ padding:'24px', textAlign:'center' }}>
              <p style={{ color:D.text2 }}>Delete <strong style={{ color:D.text }}>{delTgt.title}</strong>? This also removes the file from storage.</p>
            </div>
            <div style={mFoot}>
              <button style={btnS} onClick={() => setDel(null)}>Cancel</button>
              <button style={btnD} onClick={del}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
